import * as React from "react";
import Select, {
  StylesConfig,
  GroupBase,
  OptionsOrGroups,
} from "react-select";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CardBase from "./CardBase";
import { InputLabel } from "@mui/material";

export interface SelectOption {
  label: string;
  value: number | string;
}

interface SelectBaseProps {
  options: OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
  value?: SelectOption | SelectOption[] | null;
  onChange: (value: SelectOption | SelectOption[] | null) => void;
  label?: string;
  placeholder?: string;
  isMulti?: boolean;
  isSearchable?: boolean;
  isLoading?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  isDisabled?: boolean;
  sx?: any;
  useGlassCard?: boolean;
  glassCardTitle?: string;
  glassCardSubtitle?: string;
}

const SelectBase: React.FC<SelectBaseProps> = ({
  options,
  value,
  onChange,
  label,
  placeholder = "Select...",
  isMulti = false,
  isSearchable = true,
  isLoading = false,
  error = false,
  helperText,
  isDisabled = false,
  sx,
  useGlassCard = false,
  glassCardTitle,
  glassCardSubtitle,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLandscape = useMediaQuery("(orientation: landscape) and (max-width: 900px)");
  const isDark = theme.palette.mode === "dark";
  const baseBorderColor = isDark ? alpha("#ffffff", 0.4) : alpha("#ffffff", 0.8);
  const hoverShadow = isDark
    ? "0 12px 48px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.4)"
    : "0 12px 48px rgba(18, 144, 60, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.7)";
  const [menuPortalTarget, setMenuPortalTarget] = React.useState<HTMLElement | null>(null);
  const [isFocused, setIsFocused] = React.useState(false);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      setMenuPortalTarget(document.body);

      // Add global style to ensure menu has white background
      const style = document.createElement("style");
      style.id = "react-select-portal-fix";
      style.textContent = `
        body > div[class*="react-select"]:not([class*="menu"]) {
          background: transparent !important;
        }
        body > div[id*="react-select"]:not([class*="menu"]) {
          background: transparent !important;
        }
        /* Ensure menu has solid white background */
        div[class*="react-select__menu"],
        div[id*="react-select"] div[class*="menu"],
        div[class*="react-select"] div[class*="menu"] {
          background: #ffffff !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }
      `;
      if (!document.getElementById("react-select-portal-fix")) {
        document.head.appendChild(style);
      }

      return () => {
        const existingStyle = document.getElementById("react-select-portal-fix");
        if (existingStyle) {
          existingStyle.remove();
        }
      };
    }
  }, []);

  // Prevent auto-scroll when menu opens in landscape (simplified approach)
  React.useEffect(() => {
    if (isLandscape && isMenuOpen) {
      // Just prevent the initial auto-scroll, don't block all scrolling
      const originalScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';

      return () => {
        document.documentElement.style.scrollBehavior = originalScrollBehavior;
      };
    }
  }, [isLandscape, isMenuOpen]);

  const hasValue = React.useMemo(() => {
    if (isMulti) {
      return Array.isArray(value) && value.length > 0;
    }
    return value !== null && value !== undefined;
  }, [value, isMulti]);

  const isLabelShrunk = isFocused || hasValue;

  const controlBase = {
    background: isDark
      ? `linear-gradient(135deg, ${alpha("#ffffff", 0.15)} 0%, ${alpha("#ffffff", 0.08)} 100%)`
      : `linear-gradient(135deg, ${alpha("#ffffff", 0.25)} 0%, ${alpha("#ffffff", 0.15)} 100%)`,
    backdropFilter: "blur(40px) saturate(180%)",
    WebkitBackdropFilter: "blur(40px) saturate(180%)",
    border: `1.5px solid ${baseBorderColor}`,
    boxShadow: isDark
      ? "0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
      : "0 8px 32px rgba(18, 144, 60, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
    color: theme.palette.text.primary,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  } as const;

  const customStyles: StylesConfig<SelectOption, boolean> = {
    menuPortal: (base) => ({
      ...base,
      zIndex: theme.zIndex.modal + 10,
    }),
    control: (provided, state) => ({
      ...provided,
      minHeight: isMobile ? "36px" : "40px",
      borderRadius: isMobile ? "10px" : "12px",
      padding: "0px",
      // Responsive for mobile landscape
      "@media (orientation: landscape) and (max-width: 900px)": {
        minHeight: "32px",
        borderRadius: "8px",
      },
      borderColor: error
        ? theme.palette.error.main
        : state.isFocused
          ? alpha("#ffffff", 0.95)
          : baseBorderColor,
      boxShadow: state.isFocused
        ? `0 12px 48px ${alpha(theme.palette.primary.main, 0.2)}, inset 0 1px 0 rgba(255, 255, 255, 0.7)`
        : controlBase.boxShadow,
      transform: "none",
      "&:hover": {
        borderColor: error
          ? theme.palette.error.main
          : isDark
            ? alpha("#ffffff", 0.5)
            : alpha("#ffffff", 0.9),
      },
      background: state.isFocused
        ? isDark
          ? `linear-gradient(135deg, ${alpha("#ffffff", 0.2)} 0%, ${alpha("#ffffff", 0.12)} 100%)`
          : `linear-gradient(135deg, ${alpha("#ffffff", 0.35)} 0%, ${alpha("#ffffff", 0.22)} 100%)`
        : controlBase.background,
      backdropFilter: controlBase.backdropFilter,
      WebkitBackdropFilter: controlBase.WebkitBackdropFilter,
      border: state.isFocused
        ? `1.5px solid ${alpha("#ffffff", 0.95)}`
        : controlBase.border,
      color: controlBase.color,
      transition: controlBase.transition,
      overflow: "hidden",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: isMobile ? "8px 12px" : "20px 18px",
      overflow: "hidden",
      maxWidth: "100%",
      // Responsive for mobile landscape
      "@media (orientation: landscape) and (max-width: 900px)": {
        padding: "4px 8px",
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: theme.zIndex.modal + 10,
      background: `${isDark ? theme.palette.background.paper : "#ffffff"} !important`,
      backdropFilter: "none !important",
      WebkitBackdropFilter: "none !important",
      borderRadius: theme.shape.borderRadius * 2,
      border: `1px solid ${isDark ? theme.palette.divider : "rgba(0, 0, 0, 0.12)"}`,
      boxShadow: isDark
        ? "0 16px 40px rgba(0, 0, 0, 0.35)"
        : "0 16px 40px rgba(18, 144, 60, 0.16)",
      // Ensure menu appears below on mobile landscape
      "@media (orientation: landscape) and (max-width: 900px)": {
        maxHeight: "45vh",
        marginTop: "4px",
      },
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      maxHeight: isMobile ? "200px" : "300px",
      // Responsive maxHeight for mobile landscape
      "@media (orientation: landscape) and (max-width: 900px)": {
        maxHeight: "40vh",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? theme.palette.primary.main
        : state.isFocused
          ? theme.palette.action.hover
          : "transparent",
      color: state.isSelected
        ? theme.palette.primary.contrastText
        : theme.palette.text.primary,
      "&:active": {
        backgroundColor: theme.palette.primary.dark,
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: theme.palette.text.primary,
      margin: "0px",
      padding: "0px",
      fontSize: isMobile ? "0.875rem" : "1rem",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: "100%",
      // Responsive for mobile landscape
      "@media (orientation: landscape) and (max-width: 900px)": {
        fontSize: "0.8125rem",
      },
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: alpha(theme.palette.text.primary, 0.6),
      margin: "0px",
      padding: "0px",
      fontSize: isMobile ? "0.875rem" : "1rem",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: state.isFocused ? 0 : "100%",
      opacity: state.isFocused ? 0 : 1,
      // Responsive for mobile landscape
      "@media (orientation: landscape) and (max-width: 900px)": {
        fontSize: "0.8125rem",
      },
    }),
    input: (provided) => ({
      ...provided,
      color: theme.palette.text.primary,
      margin: "0px",
      padding: "0px",
      fontSize: isMobile ? "0.875rem" : "1rem",
      // Responsive for mobile landscape
      "@media (orientation: landscape) and (max-width: 900px)": {
        fontSize: "0.8125rem",
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#12903C",
      borderRadius: 999,
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      border: `1px solid ${alpha("#ffffff", 0.3)}`,
      fontSize: isMobile ? "0.75rem" : "0.875rem",
      height: isMobile ? "24px" : "28px",
      // Responsive for mobile landscape
      "@media (orientation: landscape) and (max-width: 900px)": {
        fontSize: "0.6875rem",
        height: "22px",
      },
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#ffffff",
      fontWeight: 600,
      fontSize: isMobile ? "0.75rem" : "0.875rem",
      padding: isMobile ? "2px 6px" : "4px 8px",
      // Responsive for mobile landscape
      "@media (orientation: landscape) and (max-width: 900px)": {
        fontSize: "0.6875rem",
        padding: "2px 4px",
      },
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#ffffff",
      "&:hover": {
        backgroundColor: "#d32f2f",
        color: "#ffffff",
      },
    }),
  };

  const selectField = (
    <>
      <Box
        sx={{
          position: "relative",
          borderRadius: { xs: "10px", sm: "12px" },
          overflow: "visible",
          mt: label ? 1 : 0,
          // Responsive for mobile landscape
          "@media (orientation: landscape) and (max-width: 900px)": {
            borderRadius: "8px",
            mt: label ? 0.5 : 0,
            "&:has(.react-select__menu)": {
              scrollMarginTop: 0,
            },
          },
        }}
      >
        {label && (
          <InputLabel
            shrink={isLabelShrunk}
            sx={{
              position: "absolute",
              left: isMobile ? "12px" : "18px",
              right: isMobile ? "12px" : "18px",
              top: isLabelShrunk ? "-8px" : "50%",
              transform: isLabelShrunk ? "translateY(0)" : "translateY(-50%)",
              transformOrigin: "top left",
              transition: "all 0.2s cubic-bezier(0.0, 0, 0.2, 1) 0ms",
              pointerEvents: "none",
              zIndex: 2,
              backgroundColor: "transparent",
              padding: isLabelShrunk
                ? "0 4px"
                : isMobile
                  ? "0 32px 0 0"
                  : "0 40px 0 0",
              color: error
                ? theme.palette.error.main
                : isFocused
                  ? theme.palette.primary.main
                  : isLabelShrunk
                    ? theme.palette.text.primary
                    : theme.palette.text.secondary,
              fontSize: isMobile
                ? (isLabelShrunk ? "0.78rem" : "0.92rem")
                : (isLabelShrunk ? "0.86rem" : "1.02rem"),
              fontWeight: isLabelShrunk ? 500 : 550,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              width: "100%",
              display: "block",
              boxSizing: "border-box",
              // Responsive for mobile landscape
              "@media (orientation: landscape) and (max-width: 900px)": {
                fontSize: isLabelShrunk ? "0.75rem" : "0.875rem",
                left: "10px",
                right: "10px",
              },
              ...(isLabelShrunk && {
                top: "-14px",
                padding: "0 8px",
                borderRadius: 999,
                width: "fit-content",
                maxWidth: "90%",
                display: "inline-flex",
                background: theme.palette.mode === "dark"
                  ? alpha("#ffffff", 0.15)
                  : alpha("#ffffff", 0.98),
                color: "#0f6d2b",
                boxShadow: theme.palette.mode === "dark"
                  ? "0 1px 4px rgba(0,0,0,0.35)"
                  : "0 1px 4px rgba(0,0,0,0.15)",
                "@media (orientation: landscape) and (max-width: 900px)": {
                  fontSize: "0.7rem",
                  top: "-12px",
                },
              }),
            }}
          >
            {label}
          </InputLabel>
        )}
        <Select<SelectOption, boolean>
          options={options}
          value={value}
          onChange={(newValue) => onChange(newValue as any)}
          isMulti={isMulti}
          isSearchable={isSearchable}
          isLoading={isLoading}
          isDisabled={isDisabled}
          placeholder={
            label
              ? isLabelShrunk
                ? placeholder
                : ""
              : placeholder
          }
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onMenuOpen={() => setIsMenuOpen(true)}
          onMenuClose={() => setIsMenuOpen(false)}
          styles={customStyles}
          menuPortalTarget={menuPortalTarget ?? undefined}
          menuPosition={isMobile ? "fixed" : "absolute"}
          menuPlacement="bottom"
          menuShouldScrollIntoView={false}
          menuShouldBlockScroll={false}
          theme={(selectTheme) => ({
            ...selectTheme,
            colors: {
              ...selectTheme.colors,
              primary: theme.palette.primary.main,
              primary75: theme.palette.primary.light,
              primary50: theme.palette.primary.light,
              primary25: theme.palette.action.hover,
            },
          })}
        />
        {/* Glass effect overlays - matching StyledTextField */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "50%",
            background: `linear-gradient(180deg, ${alpha("#ffffff", isDark ? 0.15 : 0.25)} 0%, transparent 100%)`,
            pointerEvents: "none",
            zIndex: 1,
            borderRadius: { xs: "10px 10px 0 0", sm: "12px 12px 0 0" },
            "@media (orientation: landscape) and (max-width: 900px)": {
              borderRadius: "8px 8px 0 0",
            },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, transparent 0%, transparent 100%)`,
            pointerEvents: "none",
            zIndex: 1,
            borderRadius: { xs: "10px", sm: "12px" },
            "@media (orientation: landscape) and (max-width: 900px)": {
              borderRadius: "8px",
            },
          }}
        />
      </Box>
      {error && helperText && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            color: theme.palette.error.main,
            fontSize: { xs: "0.7rem", sm: "0.75rem" },
            "@media (orientation: landscape) and (max-width: 900px)": {
              fontSize: "0.6875rem",
            },
          }}
        >
          {helperText}
        </Typography>
      )}
    </>
  );

  if (useGlassCard) {
    return (
      <CardBase
        title={glassCardTitle}
        subtitle={glassCardSubtitle}
        useGlassCard
        sx={{
          p: 0,
          ...sx,
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {selectField}
        </Box>
      </CardBase>
    );
  }

  return <Box sx={sx}>{selectField}</Box>;
};

export default SelectBase;
